import { fixtures, policyConstants } from 'tods-competition-factory';
import type { PolicyCatalogItem } from 'courthive-components';
import { POLICY_SCHEDULING } from 'assets/policies/schedulingPolicy';
import { POLICY_SCORING } from 'assets/policies/scoringPolicy';
import { POLICY_SEEDING } from 'assets/policies/seedingPolicy';
import { tmx2db } from 'services/storage/tmx2db';

const { POLICY_TYPE_SCHEDULING, POLICY_TYPE_SCORING, POLICY_TYPE_SEEDING, POLICY_TYPE_RANKING_POINTS } =
  policyConstants;

const {
  policies: {
    POLICY_RANKING_POINTS_ATP,
    POLICY_RANKING_POINTS_BASIC,
    POLICY_RANKING_POINTS_WTA,
    POLICY_RANKING_POINTS_ITF_WTT,
    POLICY_RANKING_POINTS_ITF_JUNIOR,
  },
} = fixtures;

// Federation-specific ranking policies (USTA Junior, Tennis Europe, LTA,
// Tennis Canada, Tennis Australia, CTS) used to live here as built-in
// presets. They've moved to CFS-served delivery (POLICY_DELIVERY phase 3)
// and surface via the catalog browser (phase 4). Anything user-saved
// before the cutover stays in IndexedDB and still loads via
// loadUserPolicies.

const BUILTIN_POLICIES: PolicyCatalogItem[] = [
  {
    id: 'builtin-scheduling',
    name: 'Default Scheduling',
    policyType: POLICY_TYPE_SCHEDULING,
    source: 'builtin',
    description: 'Default match scheduling times, recovery periods, and daily limits',
    policyData: POLICY_SCHEDULING[POLICY_TYPE_SCHEDULING],
  },
  {
    id: 'builtin-scoring',
    name: 'Default Scoring',
    policyType: POLICY_TYPE_SCORING,
    source: 'builtin',
    description: 'Default scoring formats and match completion rules',
    policyData: POLICY_SCORING[POLICY_TYPE_SCORING],
  },
  {
    id: 'builtin-seeding',
    name: 'Default Seeding',
    policyType: POLICY_TYPE_SEEDING,
    source: 'builtin',
    description: 'Default seeding thresholds and positioning rules',
    policyData: POLICY_SEEDING[POLICY_TYPE_SEEDING],
  },
  {
    id: 'builtin-ranking-points-basic',
    name: 'Basic Ranking Points',
    policyType: POLICY_TYPE_RANKING_POINTS,
    source: 'builtin',
    description: 'Simple finishing-position points — works for any event regardless of category or level',
    policyData: POLICY_RANKING_POINTS_BASIC[POLICY_TYPE_RANKING_POINTS],
  },
  {
    id: 'builtin-ranking-points-atp',
    name: 'ATP Ranking Points (2026)',
    policyType: POLICY_TYPE_RANKING_POINTS,
    source: 'builtin',
    description: 'PIF ATP Rankings — Grand Slams through ITF events, 15 tournament levels',
    policyData: POLICY_RANKING_POINTS_ATP[POLICY_TYPE_RANKING_POINTS],
  },
  {
    id: 'builtin-ranking-points-wta',
    name: 'WTA Ranking Points (2026)',
    policyType: POLICY_TYPE_RANKING_POINTS,
    source: 'builtin',
    description: 'PIF WTA Rankings — Grand Slams through WTA 125, with quality win bonuses',
    policyData: POLICY_RANKING_POINTS_WTA[POLICY_TYPE_RANKING_POINTS],
  },
  {
    id: 'builtin-ranking-points-itf-wtt',
    name: 'ITF World Tennis Tour Points',
    policyType: POLICY_TYPE_RANKING_POINTS,
    source: 'builtin',
    description: 'ITF World Tennis Tour — qualifying round points for $15K–$25K+H tournaments',
    policyData: POLICY_RANKING_POINTS_ITF_WTT[POLICY_TYPE_RANKING_POINTS],
  },
  {
    id: 'builtin-ranking-points-itf-junior',
    name: 'ITF Junior Circuit Points',
    policyType: POLICY_TYPE_RANKING_POINTS,
    source: 'builtin',
    description: 'ITF Junior Circuit — Grand Slams through J30, 9 levels with qualifying and consolation',
    policyData: POLICY_RANKING_POINTS_ITF_JUNIOR[POLICY_TYPE_RANKING_POINTS],
  },
];

export function getBuiltinPolicies(): PolicyCatalogItem[] {
  return BUILTIN_POLICIES;
}

export async function loadUserPolicies(): Promise<PolicyCatalogItem[]> {
  return tmx2db.findAll('policies');
}

export async function saveUserPolicy(item: PolicyCatalogItem): Promise<void> {
  await tmx2db.modifyOrAddUnique('policies', 'id', item.id, item);
}

export async function deleteUserPolicy(id: string): Promise<void> {
  await (tmx2db.dex as any).policies.where('id').equals(id).delete();
}
